import React, { useState } from 'react';
import { useQuery, useMutation } from '@apollo/client';
import { useNavigate } from 'react-router-dom';
import {
  Box, Stack, Typography, TextField, InputAdornment, Chip, Button, Alert, LinearProgress, Paper, MenuItem,
  Table, TableHead, TableRow, TableCell, Dialog, DialogTitle, DialogContent, DialogActions, Tooltip,
} from '@mui/material';
import TableBody from './PagedTableBody'; // pages rows beyond 10
import SearchIcon from '@mui/icons-material/Search';
import { VENDOR_ACCOUNTS_QUERY, REMOVE_VENDOR_SERVICE_MUTATION, USER_VERIFICATION_QUERY } from '../graphql/queries';
import DocumentReview from './DocumentReview';
import VendorPasswordDialog from './VendorPasswordDialog';
import PersonSurveys from './PersonSurveys';
import SendMessageDialog from './SendMessageDialog';
import DeleteUserDialog from './DeleteUserDialog';

// The vendor's owner ID and business document, approved one by one (then services can be added)
function VendorDocumentsDialog({ vendor, onClose, onChanged }) {
  const { data, loading, error, refetch } = useQuery(USER_VERIFICATION_QUERY, { variables: { userId: vendor.id }, fetchPolicy: 'network-only' });
  return (
    <Dialog open onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>Documents: {vendor.businessName}</DialogTitle>
      <DialogContent dividers>
        {loading && <LinearProgress />}
        {error && <Alert severity="error">{error.message}</Alert>}
        <Typography variant="body2" color="text.secondary" mb={1.5}>
          Approve the owner identity proof and a business document (GST, trade licence, Udyam or Shop &amp; Establishment). Once both are approved
          the vendor can add their services, and is notified.
        </Typography>
        {data && <DocumentReview v={data.userVerification} onChanged={() => { refetch(); onChanged(); }} />}
      </DialogContent>
      <DialogActions><Button onClick={onClose}>Close</Button></DialogActions>
    </Dialog>
  );
}

const STATUS_TEXT = {
  sent_for_review: 'awaiting the vendor’s review', user_requested_changes: 'vendor requested changes',
  user_agreed: 'awaiting signatures', draft: 'draft', signed: 'live',
};

// Admin > Vendors: the vendor user accounts (the same vendors as in Admin > Users)
export default function VendorAccountsPanel() {
  const navigate = useNavigate();
  const [search, setSearch] = useState('');
  const [show, setShow] = useState('all'); // all | live | pending | none
  const [service, setService] = useState(''); // a service category, or '' for all
  const [removing, setRemoving] = useState(null); // { vendor, service }
  const [passwordFor, setPasswordFor] = useState(null); // vendor
  const [surveysFor, setSurveysFor] = useState(null); // vendor id
  const [messaging, setMessaging] = useState(null); // vendor id
  const [deleting, setDeleting] = useState(null); // vendor
  const [documentsFor, setDocumentsFor] = useState(null); // vendor
  const [message, setMessage] = useState(null);
  const { data, loading, error, refetch } = useQuery(VENDOR_ACCOUNTS_QUERY, { fetchPolicy: 'cache-and-network' });
  const [removeService, { loading: removeBusy }] = useMutation(REMOVE_VENDOR_SERVICE_MUTATION);

  const all = data?.vendorAccounts || [];
  const q = search.trim().toLowerCase();
  const matchesShow = (v) => (
    show === 'all' ? true
      : show === 'live' ? v.services.some(s => s.live)
        : show === 'pending' ? v.services.some(s => !s.live)
          : v.services.length === 0
  );
  // Services any vendor has, for the Service filter
  const serviceOptions = [...new Map(all.flatMap(v => v.services.map(s => [s.category, s.label]))).entries()]
    .map(([category, label]) => ({ category, label })).sort((a, b) => a.label.localeCompare(b.label));
  const vendors = all.filter(v => matchesShow(v)
    && (!service || v.services.some(s => s.category === service))
    && (!q || [v.businessName, v.email, ...v.services.map(s => s.label)].some(t => (t || '').toLowerCase().includes(q))));
  const counts = {
    all: all.length,
    live: all.filter(v => v.services.some(s => s.live)).length,
    pending: all.filter(v => v.services.some(s => !s.live)).length,
    none: all.filter(v => v.services.length === 0).length,
  };

  const doRemove = async () => {
    try {
      await removeService({ variables: { vendorUserId: removing.vendor.id, category: removing.service.category } });
      setMessage({ severity: 'info', text: `${removing.service.label} removed from ${removing.vendor.businessName}. The vendor has been notified.` });
      setRemoving(null);
      refetch();
    } catch (e) { setMessage({ severity: 'error', text: e.message }); }
  };

  return (
    <Box>
      <Typography variant="body2" color="text.secondary" mb={2}>
        Vendor accounts from Admin &gt; Users. Open a vendor to choose their services and manage their offerings, packages and items.
      </Typography>
      {message && <Alert severity={message.severity} onClose={() => setMessage(null)} sx={{ mb: 2 }}>{message.text}</Alert>}
      <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
        <Stack spacing={1.5}>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
            <TextField
              size="small" sx={{ flex: 1 }} value={search} onChange={e => setSearch(e.target.value)}
              placeholder="Search by business name, email or service"
              InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> }}
            />
            <TextField select size="small" label="Service" value={service} onChange={e => setService(e.target.value)} sx={{ minWidth: 220 }}>
              <MenuItem value="">All services</MenuItem>
              {serviceOptions.map(o => <MenuItem key={o.category} value={o.category}>{o.label}</MenuItem>)}
            </TextField>
          </Stack>
          <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
            {[['all', 'All'], ['live', 'With live services'], ['pending', 'Awaiting contract'], ['none', 'No services yet']].map(([k, label]) => (
              <Chip key={k} size="small" label={`${label} (${counts[k]})`} onClick={() => setShow(k)}
                color={show === k ? 'primary' : 'default'} variant={show === k ? 'filled' : 'outlined'} />
            ))}
            <Typography variant="caption" color="text.secondary" sx={{ ml: 'auto' }}>Showing {vendors.length} of {all.length}</Typography>
          </Stack>
        </Stack>
      </Paper>
      {loading && !data && <LinearProgress sx={{ mb: 2 }} />}
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error.message}</Alert>}

      <Paper variant="outlined">
        <Box sx={{ overflowX: 'auto' }}>
          <Table size="small" sx={{ minWidth: 900 }}>
            <TableHead>
              <TableRow sx={{ '& th': { fontWeight: 700, bgcolor: 'grey.100' } }}>
                <TableCell>Vendor</TableCell>
                <TableCell>Services</TableCell>
                <TableCell align="center">Offerings</TableCell>
                <TableCell align="center">Open bookings</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {vendors.length === 0 && (
                <TableRow><TableCell colSpan={5} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                  {all.length ? 'No vendors match.' : 'No vendor accounts yet. Create one under Admin > Users with the Vendor role.'}
                </TableCell></TableRow>
              )}
              {vendors.map(v => (
                <TableRow key={v.id} hover>
                  <TableCell>
                    <Typography variant="body2" fontWeight={600}>{v.businessName}</Typography>
                    <Typography variant="caption" color="text.secondary" display="block">{v.email}</Typography>
                    {!v.isActive && <Chip size="small" label="Deactivated" sx={{ mt: 0.5, height: 18 }} />}
                    <Tooltip title={v.documentsApproved ? 'Owner ID and business document approved: can add services' : 'Services can be added once the owner ID and business document are approved'}>
                      <Chip size="small" onClick={() => setDocumentsFor(v)} sx={{ mt: 0.5, ml: 0.5, height: 18 }}
                        color={v.documentsApproved ? 'success' : v.documentsPending ? 'warning' : 'default'} variant={v.documentsApproved ? 'filled' : 'outlined'}
                        label={v.documentsApproved ? 'Documents approved' : v.documentsPending ? `${v.documentsPending} document(s) to review` : 'No documents approved'} />
                    </Tooltip>
                    {v.tempPasswordActive && (
                      <Tooltip title="Still using the temporary password from the admin">
                        <Chip size="small" color="warning" variant="outlined" label="Temp password" sx={{ mt: 0.5, ml: v.isActive ? 0 : 0.5, height: 18 }} />
                      </Tooltip>
                    )}
                  </TableCell>
                  <TableCell>
                    <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                      {v.services.length === 0 && <Typography variant="caption" color="text.secondary">No services yet</Typography>}
                      {v.services.map(s => (
                        <Tooltip key={s.category} title={`${s.contractRef || ''} · ${STATUS_TEXT[s.contractStatus] || s.contractStatus}`}>
                          <Chip
                            size="small" label={s.label}
                            color={s.live ? 'success' : 'warning'} variant={s.live ? 'filled' : 'outlined'}
                            onDelete={() => setRemoving({ vendor: v, service: s })}
                          />
                        </Tooltip>
                      ))}
                    </Stack>
                  </TableCell>
                  <TableCell align="center">{v.activeOfferings} / {v.offerings}</TableCell>
                  <TableCell align="center">{v.openBookings}</TableCell>
                  <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                    <Button size="small" sx={{ mr: 1 }} color={v.documentsPending ? 'warning' : 'primary'} onClick={() => setDocumentsFor(v)}>Documents</Button>
                    <Button size="small" sx={{ mr: 1 }} onClick={() => setMessaging(v.id)}>Message</Button>
                    <Button size="small" sx={{ mr: 1 }} onClick={() => setSurveysFor(v.id)}>Surveys</Button>
                    <Button size="small" sx={{ mr: 1 }} onClick={() => setPasswordFor(v)}>Password</Button>
                    <Button size="small" variant="contained" onClick={() => navigate(`/dashboard/admin/vendors/${v.id}`)}>
                      Manage services
                    </Button>
                    <Button size="small" color="error" sx={{ ml: 1 }} onClick={() => setDeleting(v)}>Delete</Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Box>
      </Paper>
      <Stack direction="row" spacing={2} mt={1.5}>
        <Stack direction="row" spacing={0.75} alignItems="center"><Chip size="small" color="success" label="Live" /><Typography variant="caption" color="text.secondary">signed — shown to families</Typography></Stack>
        <Stack direction="row" spacing={0.75} alignItems="center"><Chip size="small" color="warning" variant="outlined" label="Pending" /><Typography variant="caption" color="text.secondary">contract not fully signed yet</Typography></Stack>
      </Stack>

      {surveysFor && <PersonSurveys userId={surveysFor} onClose={() => setSurveysFor(null)} />}
      {messaging && <SendMessageDialog userId={messaging} onClose={() => setMessaging(null)} />}
      {documentsFor && <VendorDocumentsDialog vendor={documentsFor} onClose={() => setDocumentsFor(null)} onChanged={() => refetch()} />}
      {deleting && (
        <DeleteUserDialog user={deleting} onClose={() => setDeleting(null)}
          onDeleted={(r) => {
            setDeleting(null);
            setMessage({ severity: 'success', text: `${deleting.businessName} (${r.email}) was deleted.${r.contractsClosed ? ` ${r.contractsClosed} contract(s) closed.` : ''}${r.leadReopened ? ' Its Marketing lead can be set up again.' : ''}` });
            refetch();
          }} />
      )}
      {passwordFor && (
        <VendorPasswordDialog vendorUserId={passwordFor.id} name={passwordFor.businessName} email={passwordFor.email}
          onClose={() => setPasswordFor(null)} onDone={() => refetch()} />
      )}

      <Dialog open={Boolean(removing)} onClose={() => setRemoving(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Remove service?</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            Remove <strong>{removing?.service.label}</strong> from <strong>{removing?.vendor.businessName}</strong>?
            Their offerings for it will no longer be shown to families, and the vendor will be notified.
            Existing bookings are not cancelled.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRemoving(null)}>Cancel</Button>
          <Button color="error" variant="contained" onClick={doRemove} disabled={removeBusy}>Remove</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
